import * as Scrivito from "scrivito";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { login, logout, refresh } from "api/ABrest/ABrest";
import { UserDataQry } from "api/queries";
import { getHomepage, getPageObjAsync } from "utils";
import {
  MAX_REFRESH_ATTEMPTS,
  sessionStorageDisabledConfirmationsKey,
} from "variables";
import { gqlFetchWithAuth } from "utils/fetchData/gqlFetchWithAuth";
import type { FeatureFlagsReadonly } from "./utils/resolveFlags/resolveFlags";
import { resolveFlags } from "./utils/resolveFlags/resolveFlags";
import { parseCountryCode } from "utils/mandators/utils";

export interface User {
  id: string;
  fullName: string;
  email: string;
  tenant: string;
  currencyCode: string;
}

export interface Department {
  value?: string;
  label?: string | null;
}
interface UserState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
  department: Department | null;
  featureFlags: FeatureFlagsReadonly | null;
}

export interface UserActions {
  setDepartment: (department: Department | null) => void;
  redirectToLoginPage: () => Promise<void>;
  redirectToWelcomePage: () => Promise<void>;
  init: () => Promise<void>;
  login: (email: string, password: string) => Promise<Response>;
  logout: () => Promise<void>;
  refresh: (
    retryDelay?: number,
    attempt?: number,
  ) => Promise<boolean | undefined>;
}

type UserStoreState = UserState & { actions: UserActions };

const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  department: null,
  error: null,
  featureFlags: null,
  urqlClient: null,
};

const useUserStore = create<UserStoreState>()(
  persist(
    (set, get) => ({
      ...initialState,

      actions: {
        setDepartment: (department: Department | null) => set({ department }),
        redirectToLoginPage: async (): Promise<void> => {
          try {
            const res = await getPageObjAsync("LoginPage");
            if (!res) {
              console.error("Login page not found");
              return;
            }
            // not Scrivito.navigate because urqlClient needs full reload
            // see urql issue with clear store: https://github.com/urql-graphql/urql/issues/2511
            window.location.href = `${window.location.origin}/${res.id()}`;
          } catch (err) {
            console.error(err);
          }
        },
        redirectToWelcomePage: async (): Promise<void> => {
          const page = await Scrivito.load(() => {
            const homepage = getHomepage();
            const welcomePage = homepage?.get("welcomePage");

            if (welcomePage instanceof Scrivito.Obj) {
              return welcomePage;
            } else if (homepage instanceof Scrivito.Obj) {
              return homepage;
            }

            return null;
          });

          if (page) {
            Scrivito.navigateTo(page);
          }
        },

        init: async () => {
          try {
            const result = await gqlFetchWithAuth({
              query: UserDataQry,
            });

            if (!result.currentUser) {
              throw new Error("Current user not found");
            }

            const featureFlags = resolveFlags(
              parseCountryCode(result.currentUser?.tenant),
            );

            set({
              user: result.currentUser,
              isAuthenticated: true,
              featureFlags,
            });
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : "Unknown error";
            const err = new Error(errorMessage);
            set({
              ...initialState,
              error: err,
            });
          }
        },

        // LOGIN
        login: async (email: string, password: string): Promise<Response> => {
          set({ isLoading: true, error: null });
          try {
            const res = await login(email, password);

            if (!res.ok) {
              throw new Error(`HTTP error! Status: ${res.status}`);
            }

            await get().actions.init();

            if (get().error) {
              throw get().error;
            }

            set({ isLoading: false });

            return res;
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : "Unknown error";
            const err = new Error(errorMessage);
            set({ error: err, isLoading: false });
            throw err;
          }
        },

        // LOGOUT
        logout: async (): Promise<void> => {
          set({ isLoading: true });
          try {
            const res = await logout();

            if (res.ok) {
              sessionStorage.removeItem(sessionStorageDisabledConfirmationsKey);
              set(initialState);
              await get().actions.redirectToLoginPage();
            } else {
              throw new Error(`HTTP error! Status: ${res.status}`);
            }
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : "Unknown error";
            const err = new Error(errorMessage);
            set({ error: err, isLoading: false });
            throw err;
          }
        },

        // REFRESH
        refresh: async (
          retryDelay: number = 0,
          attempt: number = 1,
        ): Promise<boolean | undefined> => {
          const { isAuthenticated } = get();
          if (!isAuthenticated) {
            return false;
          }
          try {
            const response = await refresh();

            if (response.status !== 200) {
              sessionStorage.removeItem(sessionStorageDisabledConfirmationsKey);
              set(initialState);
              return false;
            }

            if (response.status === 200) {
              return true;
            }
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : "Unknown error";
            const err = new Error(errorMessage);
            console.error(err);
            if (attempt >= MAX_REFRESH_ATTEMPTS) {
              set(initialState);
              await get().actions.redirectToLoginPage();
              return false;
            }

            const nextRetryDelay = retryDelay ? retryDelay * 2 : 1000;

            await new Promise((resolve) => setTimeout(resolve, nextRetryDelay));

            return get().actions.refresh(nextRetryDelay, attempt + 1);
          }
        },
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        department: state.department,
      }),
    },
  ),
);

export const initUserStore = async (): Promise<void> =>
  useUserStore.getState().actions.init();

export const useIsAuthenticated = (): boolean =>
  useUserStore((state) => state.isAuthenticated);

export const useUserDepartment = (): Department | null =>
  useUserStore((state) => state.department);

export const useUser = (): User | null => useUserStore((state) => state.user);

export const useIsUserLoading = (): boolean =>
  useUserStore((state) => state.isLoading);

export const useUserError = (): Error | null =>
  useUserStore((state) => state.error);

export const useUserActions = (): UserActions =>
  useUserStore((state) => state.actions);

export const useFeatureFlags = (): FeatureFlagsReadonly | null =>
  useUserStore((state) => state.featureFlags);

export const getUserAction = () => useUserStore.getState().actions;

export const resetUserStore = () => useUserStore.setState(initialState);

export const resetAuthenticatedUserStore = () =>
  useUserStore.setState({ ...initialState, isAuthenticated: true });
export const getUser = () => useUserStore.getState().user;

export const redirectToLoginPage = () =>
  useUserStore.getState().actions.redirectToLoginPage();
